import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "apiKey",
    "workspaceId",
    "workspaceInfo",
    "workspaceName",
    "workspaceIdDisplay",
    "workspaceNameField",
    "teamId",
    "teamName",
    "teamSelect",
    "connectButton",
    "connectButtonText",
    "connectionResult",
    "tokenStatus",
    "webhookUrl",
    "webhookSecret",
    "submitButton"
  ]

  static values = {
    testUrl: String,
    hasToken: Boolean,
    workspaceName: String,
    teamName: String
  }

  connect() {
    console.log("Linear integration form controller connected")
    this.workspaceData = null

    // If we already have a token, auto-fetch workspace info on load
    if (this.hasTokenValue) {
      this.autoConnect()
    }
  }

  // Auto-connect on page load if token exists
  async autoConnect() {
    // Show that we're loading
    if (this.hasConnectButtonTextTarget) {
      this.connectButtonTextTarget.textContent = "Loading..."
    }

    try {
      const response = await fetch(this.testUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          api_key: null, // Use stored token
          workspace_id: this.hasWorkspaceIdTarget ? this.workspaceIdTarget.value : null,
          team_id: this.hasTeamIdTarget ? this.teamIdTarget.value : null
        })
      })

      const data = await response.json()

      if (response.ok && data.success && data.workspace) {
        this.updateWorkspaceInfo(data.workspace)
        if (this.hasConnectButtonTextTarget) {
          this.connectButtonTextTarget.textContent = "Refresh"
        }
      } else {
        if (this.hasConnectButtonTextTarget) {
          this.connectButtonTextTarget.textContent = "Connect"
        }
      }
    } catch (error) {
      console.error("Auto-connect failed:", error)
      if (this.hasConnectButtonTextTarget) {
        this.connectButtonTextTarget.textContent = "Connect"
      }
    }
  }

  // Called when user clicks Connect button
  async testConnection(event) {
    event.preventDefault()

    const apiKey = this.hasApiKeyTarget ? this.apiKeyTarget.value : null
    const workspaceId = this.hasWorkspaceIdTarget ? this.workspaceIdTarget.value : null
    const teamId = this.hasTeamIdTarget ? this.teamIdTarget.value : null

    // If no new API key and we don't have a stored token, show error
    if (!apiKey && !this.hasTokenValue) {
      this.showError("Please enter an API key")
      return
    }

    this.setLoading(true)

    try {
      const response = await fetch(this.testUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          api_key: apiKey || null, // null means use stored token
          workspace_id: workspaceId,
          team_id: teamId
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showSuccess("Connected successfully!")
        if (data.workspace) {
          this.updateWorkspaceInfo(data.workspace)
        }
      } else {
        this.showError(data.error || "Connection failed")
      }
    } catch (error) {
      this.showError("Network error: " + error.message)
    } finally {
      this.setLoading(false)
    }
  }

  onApiKeyInput() {
    // Clear connection result when typing
    if (this.hasConnectionResultTarget) {
      this.connectionResultTarget.innerHTML = ""
    }
  }

  updateWorkspaceInfo(workspace) {
    this.workspaceData = workspace

    // Update workspace display
    if (this.hasWorkspaceNameTarget && workspace.name) {
      this.workspaceNameTarget.textContent = workspace.name
    }

    if (this.hasWorkspaceIdTarget && workspace.id) {
      this.workspaceIdTarget.value = workspace.id
    }

    if (this.hasWorkspaceIdDisplayTarget && workspace.id) {
      this.workspaceIdDisplayTarget.textContent = `ID: ${workspace.id}`
    }

    // Store workspace name for form submission
    if (this.hasWorkspaceNameFieldTarget && workspace.name) {
      this.workspaceNameFieldTarget.value = workspace.name
    }

    // Show workspace info section
    if (this.hasWorkspaceInfoTarget) {
      this.workspaceInfoTarget.classList.remove('hidden')
    }

    // Populate team dropdown
    if (workspace.teams && workspace.teams.length > 0 && this.hasTeamSelectTarget) {
      const currentTeamId = this.hasTeamIdTarget ? this.teamIdTarget.value : null

      let options = '<option value="">Select a team...</option>'
      workspace.teams.forEach(team => {
        const selected = team.id === currentTeamId ? 'selected' : ''
        options += `<option value="${team.id}" data-name="${team.name}" ${selected}>${team.name} (${team.key})</option>`
      })

      this.teamSelectTarget.innerHTML = options

      // Add change listener
      this.teamSelectTarget.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex]
        if (this.hasTeamIdTarget) {
          this.teamIdTarget.value = e.target.value
        }
        if (this.hasTeamNameTarget && selectedOption) {
          this.teamNameTarget.value = selectedOption.dataset.name || ''
        }
      })

      // If a team was already selected, trigger update
      if (currentTeamId) {
        const selectedOption = this.teamSelectTarget.querySelector(`option[value="${currentTeamId}"]`)
        if (selectedOption && this.hasTeamNameTarget) {
          this.teamNameTarget.value = selectedOption.dataset.name || ''
        }
      }
    }
  }

  showSuccess(message) {
    if (!this.hasConnectionResultTarget) return

    this.connectionResultTarget.innerHTML = `
      <div class="alert alert-success py-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm">${message}</span>
      </div>
    `
  }

  showError(message) {
    if (!this.hasConnectionResultTarget) return

    this.connectionResultTarget.innerHTML = `
      <div class="alert alert-error py-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm">${message}</span>
      </div>
    `
  }

  setLoading(loading) {
    if (!this.hasConnectButtonTarget) return

    if (loading) {
      this.connectButtonTarget.disabled = true
      if (this.hasConnectButtonTextTarget) {
        this.connectButtonTextTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Connecting...'
      }
    } else {
      this.connectButtonTarget.disabled = false
      if (this.hasConnectButtonTextTarget) {
        this.connectButtonTextTarget.textContent = this.hasTokenValue ? 'Refresh' : 'Connect'
      }
    }
  }

  copyWebhookUrl() {
    if (!this.hasWebhookUrlTarget) return

    navigator.clipboard.writeText(this.webhookUrlTarget.value).then(() => {
      this.showCopiedFeedback(this.webhookUrlTarget)
    }).catch(err => {
      console.error('Failed to copy:', err)
    })
  }

  copyWebhookSecret() {
    if (!this.hasWebhookSecretTarget) return

    navigator.clipboard.writeText(this.webhookSecretTarget.value).then(() => {
      this.showCopiedFeedback(this.webhookSecretTarget)
    }).catch(err => {
      console.error('Failed to copy:', err)
    })
  }

  showCopiedFeedback(inputElement) {
    const originalPlaceholder = inputElement.placeholder
    const button = inputElement.parentElement.querySelector('button')
    if (button) {
      const originalHTML = button.innerHTML
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>'
      setTimeout(() => {
        button.innerHTML = originalHTML
      }, 1500)
    }
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
